import { useState, useEffect, useMemo } from "react";
import { LayoutGrid, List } from "lucide-react";
import { TOKENS, BADGE_BASE, STATUS_BADGE } from "../tokens";
import { useT } from "../theme";

const LS_KEY = "gos_list_mode";

function loadListMode() {
  try { return localStorage.getItem(LS_KEY) || "card"; } catch { return "card"; }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return Math.round((d - new Date()) / 86400000);
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// faint/line: the muted text and hairline for the current mode. They default
// to the dark palette, which is what dark and black modes have always shown.
function urgencyStyle(days, { faint = TOKENS.textDisabled, line = TOKENS.border } = {}) {
  if (days === null)
    return { text: "—",                           color: faint,  bg: "transparent",              border: line };
  if (days < 0)
    return { text: `Ended ${Math.abs(days)}d ago`, color: faint,  bg: "rgba(74,111,165,0.08)",    border: line };
  if (days === 0)
    return { text: "Ends today",                   color: TOKENS.statusRed,     bg: "rgba(239,68,68,0.12)",     border: "rgba(239,68,68,0.4)" };
  if (days <= 30)
    return { text: `${days}d left`,                color: TOKENS.statusRed,     bg: "rgba(239,68,68,0.12)",     border: "rgba(239,68,68,0.4)" };
  if (days <= 90)
    return { text: `${days}d left`,                color: TOKENS.statusAmber,   bg: "rgba(245,158,11,0.12)",    border: "rgba(245,158,11,0.4)" };
  return   { text: `${days}d left`,                color: TOKENS.statusGreen,   bg: "rgba(16,185,129,0.12)",    border: "rgba(16,185,129,0.4)" };
}

/* Render a status pill */
function StatusBadge({ label }) {
  const colorSet = STATUS_BADGE[label] || { background: "rgba(74,111,165,0.12)", color: TOKENS.textMuted, border: "1px solid rgba(74,111,165,0.25)" };
  return <span style={{ ...BADGE_BASE, ...colorSet }}>{label || "—"}</span>;
}

/* Render an urgency pill (for active grants end-date countdown) */
function UrgencyBadge({ days }) {
  const T = useT();
  const s = urgencyStyle(days, T.mode === "light" ? { faint: T.textMuted, line: T.border } : undefined);
  return (
    <span style={{
      ...BADGE_BASE,
      background: s.bg,
      color: s.color,
      border: `1px solid ${s.border}`,
      fontFamily: TOKENS.fontMono,
      fontVariantNumeric: "tabular-nums",
    }}>
      {s.text}
    </span>
  );
}

/*
 * Normalise heterogeneous item shapes into a stable display shape.
 * Returns plain data only — no JSX.
 */
function normalise(item, viewType) {
  switch (viewType) {
    case "active":
      return {
        id:          item.id,
        title:       item.shortTitle || item.title || "—",
        subtitle:    (item.shortTitle && item.title && item.shortTitle !== item.title) ? item.title : null,
        donor:       item.donor || "—",
        value:       item.fy26 || item.totalUSD || "—",
        valueLabel:  "FY26",
        dateLabel:   "Ends",
        dateVal:     fmtDate(item.end),
        statusText:  urgencyStyle(daysUntil(item.end)).text,
        accentColor: TOKENS.statusGreen,
        primeOrSub:  item.primeOrSub || "",
        pi:          item.pi || "",
        extra:       [item.states].filter(Boolean).join(" · "),
        _daysLeft:   daysUntil(item.end),
        _statusLabel: null,
        _verified:   item._verified,
        _raw:        item,
      };

    case "apps":
      return {
        id:          item.id,
        title:       item.title || "—",
        subtitle:    null,
        donor:       item.funder || "—",
        value:       item.amount || "—",
        valueLabel:  "Funding",
        dateLabel:   "Deadline",
        dateVal:     fmtDate(item.deadline),
        statusText:  item.awardStatus || "—",
        accentColor: STATUS_BADGE[item.awardStatus]?.color || TOKENS.textMuted,
        primeOrSub:  item.primeOrSub || "",
        pi:          item.pi || "",
        extra:       item.rfaNumber && item.rfaNumber !== "N/A" ? item.rfaNumber : "",
        _daysLeft:   null,
        _statusLabel: item.awardStatus || "—",
        _verified:   item._verified,
        _raw:        item,
      };

    case "terminated":
      return {
        id:          item.id,
        title:       item.title || "—",
        subtitle:    null,
        donor:       item.funder || "—",
        value:       item.awardedAmount || "—",
        valueLabel:  "Awarded",
        dateLabel:   "Period",
        dateVal:     item.year || "—",
        statusText:  "Terminated",
        accentColor: TOKENS.statusRed,
        primeOrSub:  item.primeOrSub || "",
        pi:          item.pi || "",
        extra:       [item.poc1, item.poc2].filter(Boolean).join(" · "),
        _daysLeft:   null,
        _statusLabel: "Terminated",
        _verified:   item._verified,
        _raw:        item,
      };

    case "completed":
      return {
        id:          item.id,
        title:       item.title || "—",
        subtitle:    null,
        donor:       item.funder || "—",
        value:       item.awardedAmount || "—",
        valueLabel:  "Awarded",
        dateLabel:   "Period",
        dateVal:     item.year || "—",
        statusText:  "Completed",
        accentColor: TOKENS.statusGreen,
        primeOrSub:  item.primeOrSub || "",
        pi:          item.pi || "",
        extra:       [item.poc1, item.poc2].filter(Boolean).join(" · "),
        _daysLeft:   null,
        _statusLabel: "Completed",
        _verified:   item._verified,
        _raw:        item,
      };

    default:
      return {
        id: item.id, title: "—", subtitle: null, donor: "—", value: "—", valueLabel: "",
        dateLabel: "", dateVal: "—", statusText: "—", accentColor: TOKENS.border,
        primeOrSub: "", pi: "", extra: "", _daysLeft: null, _statusLabel: null, _raw: item,
      };
  }
}

function parseAmt(str) {
  if (!str) return 0;
  return parseFloat(String(str).replace(/[^0-9.]/g, "")) || 0;
}

function sortedItems(items, col, dir, viewType) {
  const factor = dir === "asc" ? 1 : -1;
  return [...items].sort((a, b) => {
    const na = normalise(a, viewType);
    const nb = normalise(b, viewType);
    switch (col) {
      case "value":
        return (parseAmt(na.value) - parseAmt(nb.value)) * factor;
      case "date": {
        const da = new Date(na._raw.end || na._raw.deadline || na._raw.year || 0);
        const db = new Date(nb._raw.end || nb._raw.deadline || nb._raw.year || 0);
        return (da - db) * factor;
      }
      case "status":
        return String(na.statusText).localeCompare(String(nb.statusText)) * factor;
      case "donor":
        return String(na.donor).localeCompare(String(nb.donor)) * factor;
      default: // "title"
        return String(na.title).localeCompare(String(nb.title)) * factor;
    }
  });
}

/* Column definitions per viewType */
const COLS = {
  active:     [{ key: "title", label: "Grant" }, { key: "donor", label: "Donor" }, { key: "value", label: "FY26" }, { key: "date", label: "End Date" }, { key: "status", label: "Days Left" }],
  apps:       [{ key: "title", label: "Title" }, { key: "donor", label: "Funder" }, { key: "value", label: "Amount" }, { key: "date", label: "Deadline" }, { key: "status", label: "Award Status" }],
  terminated: [{ key: "title", label: "Title" }, { key: "donor", label: "Funder" }, { key: "value", label: "Awarded" }, { key: "date", label: "Period" }, { key: "status", label: "Status" }],
  completed:  [{ key: "title", label: "Title" }, { key: "donor", label: "Funder" }, { key: "value", label: "Awarded" }, { key: "date", label: "Period" }, { key: "status", label: "Status" }],
};

/*
 * GrantList — shared card/table list component for all Grant OS sub-views.
 *
 * Props:
 *   items[]           — pre-filtered/sorted items from parent
 *   viewType          — "active" | "apps" | "terminated" | "completed"
 *   T                 — theme object from useT()
 *   onCardClick(item) — opens detail panel
 *   renderRowActions  — (item) => JSX  — action buttons for each row
 *   emptyNode         — JSX shown when items is empty
 */
const PAGE_SIZE = 25;

export default function GrantList({ items = [], viewType, T, onCardClick, renderRowActions, emptyNode }) {
  const [listMode, _setListMode] = useState(loadListMode);
  const [sortCol, setSortCol]     = useState("date");
  const [sortDir, setSortDir]     = useState("asc");
  const [hov, setHov]             = useState(null);
  const [page, setPage]           = useState(1);

  const setListMode = (m) => {
    _setListMode(m);
    try { localStorage.setItem(LS_KEY, m); } catch {}
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const sorted = useMemo(
    () => sortedItems(items, sortCol, sortDir, viewType),
    [items, sortCol, sortDir, viewType]
  );

  // Reset to page 1 whenever the data set or sort order changes
  useEffect(() => { setPage(1); }, [items, sortCol, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged      = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ── Pagination controls ─────────────────────────────────────────────── */
  const renderPager = () => {
    if (sorted.length <= PAGE_SIZE) return null;
    const btnBase = {
      padding: "5px 12px", borderRadius: 6, fontSize: 12, fontFamily: TOKENS.fontUI,
      cursor: "pointer", border: `1px solid ${T.border}`, transition: "all 0.15s",
    };
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginTop: 16 }}>
        <button
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page === 1}
          style={{ ...btnBase, background: page === 1 ? "transparent" : T.bgSurface, color: page === 1 ? T.textGhost : T.textMuted, cursor: page === 1 ? "not-allowed" : "pointer" }}
        >
          ← Prev
        </button>
        <span style={{ fontSize: 12, color: T.textMuted, fontFamily: TOKENS.fontUI }}>
          Page {page} of {totalPages} <span style={{ color: T.textGhost }}>({sorted.length} total)</span>
        </span>
        <button
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page === totalPages}
          style={{ ...btnBase, background: page === totalPages ? "transparent" : T.bgSurface, color: page === totalPages ? T.textGhost : T.textMuted, cursor: page === totalPages ? "not-allowed" : "pointer" }}
        >
          Next →
        </button>
      </div>
    );
  };

  /* ── Toolbar (card/table toggle) — called as function, not component ── */
  const renderToolbar = () => (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, marginBottom: 16 }}>
      {[["card", LayoutGrid, "Card view"], ["table", List, "Table view"]].map(([mode, Icon, title]) => {
        const active = listMode === mode;
        return (
          <button
            key={mode}
            onClick={() => setListMode(mode)}
            title={title}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32, borderRadius: 6, cursor: "pointer",
              background: active ? T.bgElevated : "transparent",
              border: `1px solid ${active ? T.gold : T.border}`,
              color: active ? T.gold : T.textMuted,
              transition: "all 0.15s",
            }}
          >
            <Icon size={15} />
          </button>
        );
      })}
    </div>
  );

  /* ── Empty state ── */
  if (items.length === 0) {
    return (
      <>
        {renderToolbar()}
        {emptyNode || (
          <div style={{ textAlign: "center", color: T.textMuted, paddingTop: 64, fontFamily: TOKENS.fontUI, fontSize: 14, lineHeight: 1.6 }}>
            Nothing here yet.<br />
            <span style={{ fontSize: 12, color: T.textFaint }}>Extract a NOFO or add a grant to populate this view.</span>
          </div>
        )}
      </>
    );
  }

  /* ── CARD MODE ── */
  if (listMode === "card") {
    return (
      <>
        {renderToolbar()}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {paged.map((item) => {
            const n = normalise(item, viewType);
            const isHov = hov === n.id;
            return (
              <div
                key={n.id}
                onClick={() => onCardClick?.(item)}
                onMouseEnter={() => setHov(n.id)}
                onMouseLeave={() => setHov(null)}
                style={{
                  background: T.bgSurface,
                  border: `1px solid ${isHov ? T.slateBlue : T.border}`,
                  // "No status" uses the dark hairline colour; on a light card that
                  // read as a navy stripe, so light mode takes its own border.
                  borderLeft: `4px solid ${n.accentColor === TOKENS.border && T?.mode === "light" ? T.border : n.accentColor}`,
                  borderRadius: 8,
                  padding: "14px 18px",
                  cursor: onCardClick ? "pointer" : "default",
                  transition: "border-color 0.15s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                  {/* Left: title + meta */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, color: T.text, fontFamily: TOKENS.fontUI, fontWeight: 600, letterSpacing: "0.005em" }}>
                        {n.title}
                      </span>
                      {n.primeOrSub && (
                        <span style={{
                          fontSize: 10, padding: "2px 7px", borderRadius: 3,
                          background: "rgba(74,111,165,0.15)",
                          color: TOKENS.slateBlue,
                          border: "1px solid rgba(74,111,165,0.3)",
                          fontFamily: TOKENS.fontUI,
                        }}>
                          {n.primeOrSub}
                        </span>
                      )}
                      {n._verified === false && (
                        <span
                          title="AI-extracted — not manually reviewed. Verify deadlines and amounts before applying."
                          style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.35)", fontFamily: TOKENS.fontUI, cursor: "help" }}
                        >
                          Unverified
                        </span>
                      )}
                    </div>
                    {n.subtitle && (
                      <div style={{ fontSize: 12, color: T.textMuted, fontFamily: TOKENS.fontUI, marginBottom: 4, lineHeight: 1.5 }}>
                        {n.subtitle}
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 12 }}>
                      <span style={{ color: T.textSub || TOKENS.textSecondary, fontFamily: TOKENS.fontUI }}>{n.donor}</span>
                      {n.pi && <span style={{ color: T.textGhost, fontFamily: TOKENS.fontUI }}>{n.pi}</span>}
                      {n.extra && <span style={{ color: T.textGhost, fontFamily: TOKENS.fontUI }}>{n.extra}</span>}
                    </div>
                  </div>
                  {/* Right: value + badge */}
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, color: TOKENS.statusGreen, fontFamily: TOKENS.fontMono, fontWeight: 500, marginBottom: 2, fontVariantNumeric: "tabular-nums" }}>
                      {n.value}
                    </div>
                    <div style={{ fontSize: 10, color: T.textFaint, marginBottom: 6, fontFamily: TOKENS.fontUI, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {n.valueLabel}
                    </div>
                    {/* Status badge — active uses urgency countdown; others use status pill */}
                    {n._statusLabel
                      ? <StatusBadge label={n._statusLabel} />
                      : <UrgencyBadge days={n._daysLeft} />
                    }
                    <div style={{ fontSize: 10, color: T.textGhost, marginTop: 4, fontFamily: TOKENS.fontMono }}>
                      {n.dateLabel} {n.dateVal}
                    </div>
                  </div>
                </div>
                {renderRowActions && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }} onClick={e => e.stopPropagation()}>
                    {renderRowActions(item)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {renderPager()}
      </>
    );
  }

  /* ── TABLE MODE ── */
  const cols = COLS[viewType] || COLS.active;

  const thStyle = {
    padding: "9px 14px",
    fontSize: 11,
    fontFamily: TOKENS.fontUI,
    fontWeight: 600,
    color: T.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    borderBottom: `1px solid ${T.border}`,
    cursor: "pointer",
    whiteSpace: "nowrap",
    background: T.bgHeader,
    textAlign: "left",
    userSelect: "none",
  };

  return (
    <>
      {renderToolbar()}
      <div style={{ overflowX: "auto", borderRadius: 8, border: `1px solid ${T.border}` }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {cols.map(c => (
                <th key={c.key} style={thStyle} onClick={() => toggleSort(c.key)}>
                  {c.label}
                  <span style={{ marginLeft: 4, color: sortCol === c.key ? T.gold : T.textGhost }}>
                    {sortCol === c.key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                  </span>
                </th>
              ))}
              {renderRowActions && <th style={{ ...thStyle, cursor: "default", width: 48 }} />}
            </tr>
          </thead>
          <tbody>
            {paged.map((item, idx) => {
              const n = normalise(item, viewType);
              const isHov = hov === n.id;
              const rowBg = isHov
                ? T.bgElevated
                : idx % 2 === 0 ? T.bgSurface : T.tableRowB;

              const td = {
                padding: "10px 14px",
                borderBottom: `1px solid ${T.border}`,
                background: rowBg,
                verticalAlign: "middle",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: onCardClick ? "pointer" : "default",
              };

              return (
                <tr key={n.id} onMouseEnter={() => setHov(n.id)} onMouseLeave={() => setHov(null)}>
                  {/* Title */}
                  <td style={{ ...td, maxWidth: 280 }} onClick={() => onCardClick?.(item)}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                      <div style={{ fontFamily: TOKENS.fontUI, fontWeight: 600, fontSize: 13, color: T.text, overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>
                        {n.title}
                      </div>
                      {n._verified === false && (
                        <span
                          title="AI-extracted — not manually reviewed. Verify deadlines and amounts before applying."
                          style={{ fontSize: 10, padding: "2px 6px", borderRadius: 3, background: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.35)", fontFamily: TOKENS.fontUI, cursor: "help", flexShrink: 0 }}
                        >
                          Unverified
                        </span>
                      )}
                    </div>
                    {n.primeOrSub && (
                      <div style={{ fontSize: 10, color: TOKENS.slateBlue, marginTop: 2, fontFamily: TOKENS.fontUI }}>
                        {n.primeOrSub}
                      </div>
                    )}
                  </td>
                  {/* Donor / Funder */}
                  <td style={{ ...td, maxWidth: 160, color: T.textSub || TOKENS.textSecondary, fontFamily: TOKENS.fontUI, fontSize: 13 }} onClick={() => onCardClick?.(item)}>
                    {n.donor}
                  </td>
                  {/* Value */}
                  <td style={{ ...td, color: TOKENS.statusGreen, fontFamily: TOKENS.fontMono, fontSize: 12, fontVariantNumeric: "tabular-nums" }} onClick={() => onCardClick?.(item)}>
                    {n.value}
                  </td>
                  {/* Date */}
                  <td style={{ ...td, fontFamily: TOKENS.fontMono, fontSize: 12, color: T.textSub, fontVariantNumeric: "tabular-nums" }} onClick={() => onCardClick?.(item)}>
                    {n.dateVal}
                  </td>
                  {/* Status badge — active uses urgency countdown; others use status pill */}
                  <td style={{ ...td }} onClick={() => onCardClick?.(item)}>
                    {n._statusLabel
                      ? <StatusBadge label={n._statusLabel} />
                      : <UrgencyBadge days={n._daysLeft} />
                    }
                  </td>
                  {renderRowActions && (
                    <td style={{ ...td, cursor: "default", textAlign: "right" }} onClick={e => e.stopPropagation()}>
                      {renderRowActions(item)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {renderPager()}
    </>
  );
}
